import isNil from 'lodash/isNil';
import flatten from 'lodash/flatten';
import { Author } from '../../../../../models/Product';
import { CurrentProductList } from './SortingView';
import { LoanFormat, mapLoanFormat } from '../../../../../models/LoanFormat';
import { TFunction } from 'i18next';

export function isProductInList(product: CurrentProductList, list: CurrentProductList[]) {
    return list.some(curr => JSON.stringify(product) === JSON.stringify(curr));
}

export function createNameList(performers: Author[]) {
    if (isNil(performers) || !Array.isArray(performers)) {
        console.error('"performers" needs to be a list of objects.');
        return null;
    }
    return performers.reduce((acc: string, curr: Author, index: number) => {
        if (!curr.name) return acc;
        acc += curr.name;
        if (index < performers.length - 1) {
            acc += ', ';
        }
        return acc;
    }, '');
}

export function getFullName(firstName?: string, lastName?: string) {
    let fullName = '';

    if (!isNil(firstName) && !isNil(lastName)) {
        fullName = `${firstName} ${lastName}`;
    } else if (isNil(lastName)) {
        fullName = `${firstName}`;
    } else if (isNil(firstName)) {
        fullName = `${lastName}`;
    }
    return fullName;
}

export function isValideEpochMsTimestamp(input: unknown) {
    return Number.isInteger(input);
}

export function formatEpochMsToDisplayDate(epochMs: number, t: TFunction) {
    const isValidTimestamp = isValideEpochMsTimestamp(epochMs);
    if (!isValidTimestamp) {
        console.error(`Param "${epochMs}" has to be a number (Epoch MS)`);
        return '';
    }
    return t('dateFormat.dateWithShortMonth', {
        val: new Date(epochMs)
    });
}

export function reorderProductsInList(
    productList: CurrentProductList[],
    productsToMove: CurrentProductList[],
    startPosition: number
) {
    const isInputValid = isValidInputForReorderProdcutsInList(productList, productsToMove, startPosition);
    if (!isInputValid) return;

    const productsToMoveSorted = productsToMove.sort((a, b) => a.rank! - b.rank!);

    return flatten(
        productList
            .map(product => {
                if (isProductInList(product, productsToMove)) {
                    return null;
                }
                return product;
            })
            .map((product, index) => {
                if (index === startPosition) return [...productsToMoveSorted, product];
                if (index === startPosition - 1 && startPosition === productList.length)
                    return [product, ...productsToMoveSorted];
                return product;
            })
    )
        .filter((product: CurrentProductList) => product !== null)
        .map((product: CurrentProductList, index: number) => {
            return { ...product, rank: index };
        });
}

function isValidInputForReorderProdcutsInList(
    productList: CurrentProductList[],
    productsToMove: CurrentProductList[],
    startPosition: number
) {
    if (!Array.isArray(productList)) {
        console.error('"productList" needs to be set and an array.');
        return false;
    }

    if (!Array.isArray(productsToMove)) {
        console.error('"productsToMove" needs to be set and an array.');
        return false;
    }

    if (!Number.isInteger(startPosition)) {
        console.error('"startPosition" needs to be set and an integer.');
        return false;
    }

    if (startPosition > productList.length) {
        console.error('"startPosition" can not be bigger then the length of "productList".');
        return false;
    }
    return true;
}

function getLoanFormatAmount(loanFormat, productList) {
    if (loanFormat === '') {
        return productList?.length;
    }

    return productList?.reduce(
        (acc, curr) => (curr && mapLoanFormat(curr.loanFormat) === loanFormat ? acc + 1 : acc),
        0
    );
}

export const getLoanFormatAllAmount = getLoanFormatAmount.bind(null, '');
export const getLoanFormatEbooksAmount = getLoanFormatAmount.bind(null, LoanFormat.eBooks);
export const getLoanFormatEAudiobooksAmount = getLoanFormatAmount.bind(null, LoanFormat.eAudiobooks);
export const getLoanFormatEMagazineAmount = getLoanFormatAmount.bind(null, LoanFormat.ePress);
